import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TransferTicketRequest } from '@packages/types/requestTypes';
import { getOrderQuery, getTicketQuery, QUERY_KEYS } from '@/api/queries';
import { FirebaseClient } from '@/api/firebaseClient';
import { useInvalidatingMutation } from '@/hooks/useInvalidatingMutation';
import { useEvent } from '@/layouts/eventContext';
import { formatEventNumericDate } from '@/features/events/eventFormatting';
import { getAdminCheckinUrl } from '@/features/ticketing/adminCheckinUrl';
import { formatEur } from '@/features/ticketing/format';
import {
  Poster,
  QR,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  resolveEventTheme,
} from '@/components';

const client = new FirebaseClient();

export const EventTicketDetails = () => {
  const { t, i18n } = useTranslation();
  const event = useEvent();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { data: ticket } = useQuery(getTicketQuery(ticketId ?? ''));
  const { data: order } = useQuery(getOrderQuery(ticket?.orderId ?? ''));

  const [showTransfer, setShowTransfer] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [transferred, setTransferred] = useState(false);

  const { mutateAsync: transferTicketFn } = useInvalidatingMutation([QUERY_KEYS.TICKETS], {
    mutationFn: async (data: TransferTicketRequest) => client.transferTicket(data),
  });

  const submitTransfer = async () => {
    const form = document.getElementById('eventTicketTransferForm') as HTMLFormElement | null;
    if (!form?.reportValidity() || !ticket) return;
    try {
      await transferTicketFn({ name, email, ticketId: ticket.ticketId, orderId: ticket.orderId });
      setName('');
      setEmail('');
      setShowTransfer(false);
      setTransferred(true);
    } catch (e) {
      console.error('transfer failed', e);
    }
  };

  if (!ticket || !order) return null;
  const paid = Boolean(order.order.paidAt);
  const poster = event.posterUrl;
  const modalTheme = resolveEventTheme(event.theme);
  const checkinUrl = getAdminCheckinUrl({ kind: 'event', eventId: event.eventId }, ticket.ticketId);

  const meta: [string, string][] = [
    [t('ticket'), ticket.ticketType.label],
    [t('price'), formatEur(ticket.price, i18n.language)],
    [t('name'), ticket.name],
    [t('email'), ticket.email],
    [t('orderNumber'), ticket.orderId],
  ];

  return (
    <div className="relative z-[2] mx-auto grid w-full max-w-[1080px] grid-cols-1 items-start gap-9 px-5 py-11 sm:px-12 lg:grid-cols-[minmax(380px,520px)_minmax(320px,420px)] lg:gap-16">
      <div className="min-w-0">
        <div className="ev-eyebrow">{t('ticketStatus')}</div>
        <h1 className="ev-disp uppercase" style={{ fontSize: 50, marginTop: 8, marginBottom: 14 }}>
          {t('your_ticket')}
        </h1>

        {ticket.canceledAt ? (
          <div className="ev-glass p-6" style={{ borderColor: 'var(--ev-accent2)' }}>
            <div className="ev-disp" style={{ fontSize: 24 }}>
              {t('ticketCanceled')}
            </div>
            <p className="ev-mono mt-2 text-sm" style={{ color: 'var(--ev-muted)' }}>
              {t('ticketCanceled_message')}
            </p>
          </div>
        ) : (
          <>
            <span className={`ev-tag ${paid ? 'ev-tag--solid' : ''} mb-5`}>
              {paid
                ? `✓ ${t('paymentReceivedOn')} ${formatEventNumericDate(order.order.paidAt)}`
                : t('paymentPending')}
            </span>

            <div className="grid grid-cols-1 items-start gap-6">
              <div className="min-w-0">
                {paid ? (
                  <div
                    className="ev-glass w-full p-5"
                    style={{ borderColor: 'color-mix(in srgb, var(--ev-ink) 28%, transparent)' }}>
                    <div
                      className="mx-auto flex w-fit justify-center"
                      style={{
                        background: '#f4f2ee',
                        padding: 14,
                        border: '2px solid var(--ev-ink)',
                        boxShadow: '4px 4px 0 var(--ev-accent)',
                      }}>
                      <QR
                        size={240}
                        value={checkinUrl}
                        dark="#1b1812"
                        light="#f4f2ee"
                      />
                    </div>
                    <div
                      className="mt-5 break-all text-center font-[var(--ev-mono)] text-[15px] tracking-[0.12em]"
                      style={{ color: 'var(--ev-ink)' }}>
                      {ticket.ticketId}
                    </div>
                    <div
                      className="mt-4 text-center font-[var(--ev-mono)] text-[11px] uppercase tracking-[0.12em]"
                      style={{
                        borderTop: '1px dashed var(--ev-line)',
                        color: 'var(--ev-muted)',
                        paddingTop: 14,
                      }}>
                      {t('scan_at_entrance')}
                    </div>
                  </div>
                ) : (
                  <div className="ev-glass p-6">
                    <p className="ev-mono text-sm" style={{ color: 'var(--ev-muted)' }}>
                      {t('paymentPending_message')}
                    </p>
                    <Link
                      to={`/events/${event.slug}/orders/${ticket.orderId}`}
                      className="ev-btn ev-btn--solid2 mt-4">
                      {t('payNow')} →
                    </Link>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-3.5 lg:flex-col">
                  {paid && !transferred ? (
                    <button
                      type="button"
                      className="ev-btn ev-btn--ghost"
                      onClick={() => setShowTransfer(true)}>
                      {t('transfer_ticket')}
                    </button>
                  ) : null}
                  <Link
                    to={`/events/${event.slug}/orders/${ticket.orderId}`}
                    className="ev-btn ev-btn--ghost">
                    {t('gotoOrder')}
                  </Link>
                </div>
                {transferred ? (
                  <p className="ev-mono mt-3 text-sm" style={{ color: 'var(--ev-accent)' }}>
                    {t('ticket_transfer_successful')}
                  </p>
                ) : null}
              </div>

              <div
                className="ev-glass min-w-0 p-5"
                style={{ borderColor: 'color-mix(in srgb, var(--ev-ink) 20%, transparent)' }}>
                <dl className="m-0 min-w-0">
                  {meta.map(([k, v]) => (
                    <div
                      key={k}
                      className="flex justify-between gap-4 py-3 first:pt-0 last:pb-0"
                      style={{ borderBottom: '1px solid var(--ev-line)' }}>
                      <dt
                        className="ev-mono shrink-0 uppercase"
                        style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--ev-muted)' }}>
                        {k}
                      </dt>
                      <dd
                        className={`min-w-0 text-right ${
                          v === ticket.orderId || k === t('price') ? 'ev-mono' : ''
                        } ${k === t('price') ? 'whitespace-nowrap' : ''}`}
                        style={{ margin: 0, fontSize: 16, color: 'var(--ev-ink)' }}>
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </>
        )}
      </div>

      {poster ? (
        <aside
          className="mx-auto w-full max-w-[380px] lg:sticky lg:top-5 lg:mx-0 lg:justify-self-start"
          style={{ maxWidth: 'min(100%, 420px, calc((100vh - 150px) * 0.75))' }}>
          <Poster src={poster} title={event.title} label="poster" />
        </aside>
      ) : null}

      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          overlayClassName="bg-black/45 backdrop-blur-md"
          closeClassName="bg-[color-mix(in_srgb,var(--ev-bg)_92%,var(--ev-ink))] text-[var(--ev-ink)] hover:bg-[var(--ev-accent)] hover:text-[var(--ev-accent-ink)]"
          className="max-w-lg border-[2px] p-7 font-[var(--ev-sans)] text-[var(--ev-ink)] shadow-[6px_6px_0_var(--ev-ink)] [border-color:var(--ev-line)]"
          style={{
            ...modalTheme,
            background: 'color-mix(in srgb, var(--ev-bg) 92%, var(--ev-ink))',
            backdropFilter: 'none',
          }}>
          <DialogHeader>
            <DialogTitle
              className="pr-8 text-[28px] font-extrabold uppercase leading-tight tracking-[-0.02em] text-[var(--ev-ink)]"
              style={{ fontFamily: 'var(--ev-sans)' }}>
              {t('transfer_ticket_title')}
            </DialogTitle>
            <DialogDescription className="font-[var(--ev-sans)] text-sm leading-relaxed text-[var(--ev-muted)]">
              {t('transfer_ticket_description')}
            </DialogDescription>
          </DialogHeader>
          <form id="eventTicketTransferForm" className="flex w-full flex-col gap-3 py-2">
            <label className="block">
              <span className="ev-label">{t('full_name')}</span>
              <input
                className="ev-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('full_name')}
                style={{ background: 'color-mix(in srgb, var(--ev-bg) 84%, white)' }}
              />
            </label>
            <label className="block">
              <span className="ev-label">E-Mail</span>
              <input
                className="ev-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mail.de"
                style={{ background: 'color-mix(in srgb, var(--ev-bg) 84%, white)' }}
              />
            </label>
          </form>
          <DialogFooter>
            <button
              type="button"
              className="ev-btn ev-btn--ghost"
              onClick={() => setShowTransfer(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="ev-btn" onClick={submitTransfer}>
              {t('transfer_ticket')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
